import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/Button";
import { cn } from "@/shared/lib/utils";

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages (>= 1). */
  pageCount: number;
  onPageChange: (page: number) => void;
  className?: string;
}

type PageItem = number | "ellipsis";

/**
 * Build the windowed list of page items to render: always the first and last
 * page, the current page and its immediate neighbours, with `"ellipsis"`
 * markers collapsing the gaps. A single-page gap is shown as that page rather
 * than an ellipsis (no point hiding one number behind a "…").
 */
export function getPageItems(page: number, pageCount: number): PageItem[] {
  if (pageCount <= 1) return [1];

  // Small page counts fit comfortably — show every page rather than hiding a
  // couple behind an ellipsis.
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const wanted = new Set<number>([1, pageCount]);
  for (let p = page - 1; p <= page + 1; p++) {
    if (p >= 1 && p <= pageCount) wanted.add(p);
  }

  const sorted = [...wanted].sort((a, b) => a - b);
  const items: PageItem[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev === 2) items.push(prev + 1);
    else if (p - prev > 1) items.push("ellipsis");
    items.push(p);
    prev = p;
  }
  return items;
}

/**
 * Numbered page list with Previous / Next controls. Built entirely from the
 * Button primitive (per design.md §10 — no raw `<button>`). The caller owns
 * page state; this component is presentational.
 */
export function Pagination({
  page,
  pageCount,
  onPageChange,
  className,
}: PaginationProps) {
  if (pageCount <= 1) return null;

  const items = getPageItems(page, pageCount);
  const atStart = page <= 1;
  const atEnd = page >= pageCount;

  return (
    <nav
      aria-label="Pagination"
      className={cn("flex items-center justify-center gap-1.5", className)}
    >
      <Button
        variant="outline"
        size="icon"
        onClick={() => onPageChange(page - 1)}
        disabled={atStart}
        aria-label="Previous page"
      >
        <ChevronLeft aria-hidden className="h-4 w-4" />
      </Button>

      {items.map((item, i) =>
        item === "ellipsis" ? (
          <span
            key={`ellipsis-${i}`}
            aria-hidden
            className="inline-flex h-10 w-10 items-center justify-center type-body-sm text-text-muted"
          >
            …
          </span>
        ) : (
          <Button
            key={item}
            variant={item === page ? "default" : "ghost"}
            size="icon"
            onClick={() => onPageChange(item)}
            aria-label={`Page ${item}`}
            aria-current={item === page ? "page" : undefined}
          >
            {item}
          </Button>
        ),
      )}

      <Button
        variant="outline"
        size="icon"
        onClick={() => onPageChange(page + 1)}
        disabled={atEnd}
        aria-label="Next page"
      >
        <ChevronRight aria-hidden className="h-4 w-4" />
      </Button>
    </nav>
  );
}
